import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Calculator, CheckCircle, PaperPlaneTilt, DownloadSimple,
  CheckCircle as Check, Circle, Clock, FileCsv, EnvelopeSimple, FileZip,
} from "@phosphor-icons/react";
import api, { API, formatApiError } from "@/lib/api";
import { RM, RMcompact, MONTHS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { StatCard } from "@/components/StatCard";
import { AttendanceDialog } from "@/components/AttendanceDialog";
import { Button } from "@/components/ui/button";

const STEPS = [
  { key: "draft", label: "Draft" },
  { key: "review", label: "Review" },
  { key: "approved", label: "Approved" },
  { key: "disbursed", label: "Disbursed" },
];
const ORDER = { draft: 0, review: 1, approved: 2, disbursed: 3 };

export default function PayrollRunDetail() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isApprover = user?.role === "approver";
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attItem, setAttItem] = useState(null);

  const load = useCallback(() => {
    api.get(`/payroll/runs/${runId}`).then(({ data }) => setRun(data)).catch(() => navigate("/payroll"));
  }, [runId, navigate]);
  useEffect(() => { load(); }, [load]);

  const calculate = async () => {
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${runId}/calculate`);
      toast.success("Payroll calculated for all active employees");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/status?status=${status}`);
      if (status === "disbursed" && data.email_result) {
        toast.success(`Run disbursed · ${data.email_result.sent} payslip email(s) sent`);
      } else {
        toast.success(`Run ${status}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const emailPayslips = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/email-payslips`);
      toast.success(`${data.sent} sent · ${data.skipped_no_email} without email${data.failed ? ` · ${data.failed} failed` : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const downloadPayslip = (emp) => {
    const token = localStorage.getItem("pf_token");
    fetch(`${API}/payroll/runs/${runId}/payslip/${emp.employee_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `payslip_${emp.employee_no}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not download payslip"));
  };

  const downloadGiro = () => {
    const token = localStorage.getItem("pf_token");
    fetch(`${API}/payroll/runs/${runId}/giro`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `giro_${run.year}_${String(run.month).padStart(2, "0")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not download giro file"));
  };

  const downloadZip = () => {
    const token = localStorage.getItem("pf_token");
    fetch(`${API}/payroll/runs/${runId}/payslips/zip`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `payslips_${run.year}_${String(run.month).padStart(2, "0")}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Could not download payslips"));
  };

  if (!run)
    return <Layout title="Payroll Run"><div className="h-64 animate-pulse rounded-md bg-[#FFFFFF]" /></Layout>;

  const s = run.summary;
  const step = ORDER[run.status];
  const hasItems = run.items.length > 0;

  return (
    <Layout
      title={`${MONTHS[run.month]} ${run.year} Payroll`}
      subtitle={`Pay date ${run.pay_date}`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/payroll")} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5]">
            <ArrowLeft size={15} className="mr-1.5" /> Back
          </Button>
          {(run.status === "draft" || run.status === "review") && isAdmin ? (
            <Button data-testid="calculate-btn" onClick={calculate} disabled={busy} className="bg-red-500 font-semibold text-white hover:bg-red-600">
              <Calculator size={16} className="mr-1.5" /> {hasItems ? "Recalculate" : "Calculate"}
            </Button>
          ) : null}
          {run.status === "review" && hasItems && isApprover && (
            <Button data-testid="approve-btn" onClick={() => setStatus("approved")} disabled={busy} className="bg-emerald-500 font-semibold text-white hover:bg-emerald-600">
              <CheckCircle size={16} className="mr-1.5" /> Approve
            </Button>
          )}
          {run.status === "review" && hasItems && isAdmin && (
            <span className="rounded-md border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs text-blue-400">
              Awaiting approver sign-off
            </span>
          )}
          {run.status === "approved" && isAdmin && (
            <Button data-testid="disburse-btn" onClick={() => setStatus("disbursed")} disabled={busy} className="bg-blue-600 font-semibold text-white hover:bg-blue-600">
              <PaperPlaneTilt size={16} className="mr-1.5" /> Disburse
            </Button>
          )}
          {(run.status === "approved" || run.status === "disbursed") && isAdmin && (
            <Button data-testid="giro-btn" variant="outline" onClick={downloadGiro} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-emerald-400">
              <FileCsv size={16} className="mr-1.5" /> Giro File
            </Button>
          )}
          {run.status === "disbursed" && isAdmin && (
            <Button data-testid="email-payslips-btn" variant="outline" onClick={emailPayslips} disabled={busy} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-violet-400">
              <EnvelopeSimple size={16} className="mr-1.5" /> Email Payslips
            </Button>
          )}
          {hasItems && isAdmin && (
            <Button data-testid="download-zip-btn" variant="outline" onClick={downloadZip} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#EEF1F5] hover:text-red-500">
              <FileZip size={16} className="mr-1.5" /> Download All (ZIP)
            </Button>
          )}
        </div>
      }
    >
      {/* Stepper */}
      <div className="mb-6 flex items-center gap-4 border border-[#E2E5EA] bg-[#FFFFFF] px-6 py-4">
        {STEPS.map((st, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <div key={st.key} className="flex flex-1 items-center gap-3">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                done ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                : active ? "border-red-500 bg-red-500/10 text-red-500"
                : "border-[#E2E5EA] text-[#6B7280]"}`}>
                {done ? <Check size={16} weight="fill" /> : <Circle size={10} weight={active ? "fill" : "regular"} />}
              </div>
              <span className={`text-sm font-medium ${active ? "text-red-500" : done ? "text-emerald-400" : "text-[#6B7280]"}`}>{st.label}</span>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < step ? "bg-emerald-500" : "bg-[#E2E5EA]"}`} />}
            </div>
          );
        })}
        <StatusBadge status={run.status} testId="detail-status" />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Gross Payroll" value={RMcompact(s.gross)} tone="white" />
        <StatCard label="Net Payroll" value={RMcompact(s.net_pay)} tone="amber" />
        <StatCard label="Statutory Total" value={RMcompact(s.statutory_total)} tone="blue" />
        <StatCard label="Employer Cost" value={RMcompact(s.employer_cost)} tone="green" />
      </div>

      {!hasItems ? (
        <div className="mt-6 flex flex-col items-center justify-center border border-dashed border-[#E2E5EA] bg-[#FFFFFF] py-16 text-center">
          <Calculator size={36} className="text-[#C7CDD6]" />
          <p className="mt-3 font-heading text-lg font-semibold text-[#0F172A]">Not calculated yet</p>
          <p className="mt-1 max-w-sm text-sm text-[#9CA3AF]">Run the calculation to compute EPF, SOCSO, EIS and PCB for all active employees.</p>
          {isAdmin && (
            <Button onClick={calculate} disabled={busy} className="mt-5 bg-red-500 font-semibold text-white hover:bg-red-600">
              <Calculator size={16} className="mr-1.5" /> Calculate Payroll
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto border border-[#E2E5EA] bg-[#FFFFFF]">
          <table className="w-full text-sm" data-testid="payroll-items-table">
            <thead>
              <tr className="border-b border-[#E2E5EA] text-left text-xs uppercase tracking-wider text-[#6B7280]">
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 text-right font-medium">Gross</th>
                <th className="px-4 py-3 text-right font-medium">OT</th>
                <th className="px-4 py-3 text-right font-medium">EPF</th>
                <th className="px-4 py-3 text-right font-medium">SOCSO</th>
                <th className="px-4 py-3 text-right font-medium">EIS</th>
                <th className="px-4 py-3 text-right font-medium">PCB</th>
                <th className="px-4 py-3 text-right font-medium">Net Pay</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {run.items.map((it) => (
                <tr key={it.id} data-testid={`item-${it.employee_no}`} className="border-b border-[#EDEFF2] transition-colors duration-150 hover:bg-[#EEF1F5]">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#0F172A]">{it.employee_name}</p>
                    <p className="text-xs text-[#6B7280]">
                      {it.employee_no} · {it.department}
                      {it.unpaid_leave_days > 0 && <span className="ml-1 text-red-400">· {it.unpaid_leave_days}d unpaid</span>}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#0F172A]">{RM(it.gross)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#9CA3AF]">{it.overtime_hours > 0 ? `${it.overtime_hours}h` : "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#9CA3AF]">{RM(it.epf_employee)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#9CA3AF]">{RM(it.socso_employee)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#9CA3AF]">{RM(it.eis_employee)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#9CA3AF]">{RM(it.pcb)}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-red-500">{RM(it.net_pay)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {isAdmin && (run.status === "draft" || run.status === "review") && (
                        <button data-testid={`attendance-${it.employee_no}`} onClick={() => setAttItem(it)} title="Attendance & overtime" className="rounded p-1.5 text-[#9CA3AF] hover:bg-[#E2E5EA] hover:text-red-500">
                          <Clock size={16} />
                        </button>
                      )}
                      <button data-testid={`payslip-${it.employee_no}`} onClick={() => downloadPayslip(it)} title="Payslip PDF" className="rounded p-1.5 text-[#9CA3AF] hover:bg-[#E2E5EA] hover:text-emerald-400">
                        <DownloadSimple size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AttendanceDialog open={!!attItem} onOpenChange={(o) => !o && setAttItem(null)} item={attItem} onSaved={load} />
    </Layout>
  );
}
